import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IAnalyserData, IAnalyserDisplayData } from "../../../../interface/analysed";
import { Toast } from "primereact/toast";
import { ColumnType, Icons, MEMBER_KEY, Messages } from "../../../../utils/constants";
import { formatCurrency } from "../../../../utils/commonUtil";
import { confirmPopup } from "primereact/confirmpopup";
import { useHttpGets } from "../../../../services/httpService";
import { ApiEndPoints } from "../../../../utils/endpoints";
import { REIDataTable, REIToastContainer } from "../../../../components/ui";
import { useNavigate } from "react-router-dom";

interface IRecentlyAnalysedProps {
  data: IAnalyserData[];
  forecastForYears: number;
  callRefreshData: () => void;
  hideExtraColumns?: boolean;
}

const RecentlyAnalysed: React.FC<IRecentlyAnalysedProps> = ({
  data,
  forecastForYears,
  callRefreshData,
  hideExtraColumns,
}) => {
  const toastRef = useRef<Toast>(null);
  const [recentlyAnalysedProperties, setRecentlyAnalysedProperties] = useState<IAnalyserDisplayData[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (data && data.length > 0) {
      setRecentlyAnalysedProperties(getRecentlyAnalysedata());
    }
  }, [data, forecastForYears]);

  const getRecentlyAnalysedata = useCallback((): IAnalyserDisplayData[] => {
    return data.reduce((analysedData: IAnalyserDisplayData[], analysedObject: IAnalyserData) => {
      if (forecastForYears >= 0 && forecastForYears < analysedObject.PositionData.length) {
        const positionData = analysedObject.PositionData[forecastForYears];
        analysedData.push({
          Country: analysedObject.Country,
          ModulesId: analysedObject.ModulesId,
          CapitalGrowth: positionData.CapitalGrowth,
          CashflowPostTax: positionData.CashflowPostTax,
          CashflowPreTax: positionData.CashflowPreTax,
          GrossYield: positionData.GrossYield,
          LoanAmount: positionData.LoanAmount,
          MarketValue: positionData.MarketValue,
          NetYield: positionData.NetYield,
          PropertyAnalyserId: analysedObject.PropertyAnalyserId,
          PropertyName: analysedObject.PropertyName,
          PropertyAddress: analysedObject.PropertyAddress || analysedObject.PropertyName,
        });
      }
      return analysedData;
    }, []);
  }, [data, forecastForYears]);

  const deleteRecentlyAnalysedProperty = async (rowData: IAnalyserDisplayData) => {
    try {
      const response = await useHttpGets(
        `${ApiEndPoints.analyser}/RemovePropertyAnalyserRecord?MemberId=${localStorage.getItem(
          MEMBER_KEY
        )}&PropertAnalyserId=${rowData.PropertyAnalyserId}`
      );
      if (response.status === 200) {
        callRefreshData();
        showToast("success", Messages.SUCCESS, Messages.DELETE_SUCCESS);
      } else {
        showToast("error", "Error", Messages.DELETE_ERROR);
      }
    } catch (error) {
      showToast("error", "Error", Messages.DELETE_ERROR);
    }
  };

  const handleRecentlyAnalysedProperty = (event: React.MouseEvent<HTMLElement>, rowData: IAnalyserDisplayData) => {
    confirmPopup({
      target: event.currentTarget,
      message: Messages.CONFIRMATION_MESSAGE,
      icon: Icons.INFO_ICON,
      accept: () => deleteRecentlyAnalysedProperty(rowData),
      reject: () => {},
    });
  };

  const handlePropertyClick = (event: React.MouseEvent<HTMLElement>, rowData: IAnalyserDisplayData) => {
    navigate("/property-analyser/analysis-report", {
      state: {
        propertyAnalyserId: rowData.PropertyAnalyserId,
        propertyName: rowData.PropertyName,
        propertyAddress: rowData.PropertyAddress,
        countryID: rowData.Country === "AU" ? 1 : 2,
      },
    });
  };

  const showToast = (severity: "success" | "info" | "warn" | "error", summary: string, detail: string) => {
    toastRef.current?.show({ severity, summary, detail, life: 3000 });
  };

  const currencyTemplate = (field: keyof IAnalyserDisplayData, rowData: IAnalyserDisplayData) => (
    <div className={`w-[92px] text-end`}>
      {(rowData[field] as number) < 0 ? (
        <span className="text-red-600">({formatCurrency(-rowData[field] as number)})</span>
      ) : (
        formatCurrency(rowData[field] as number)
      )}
    </div>
  );

  const cashflowPreTaxTemplate = (rowData: IAnalyserDisplayData) => (
    <div className="w-[130px] text-end">
      {rowData.CashflowPreTax < 0 ? (
        <span className="text-red-600">({formatCurrency(-rowData.CashflowPreTax)})</span>
      ) : (
        formatCurrency(rowData.CashflowPreTax)
      )}
    </div>
  );

  const netYieldPreTaxTemplate = (rowData: IAnalyserDisplayData) => (
    <div className="w-[150px] text-end">{rowData.GrossYield < 0 ? `0%` : `${rowData.GrossYield}%`}</div>
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "PropertyAddress",
        header: "Property Name",
        type: "link",
        width: "55%",
        onClick: handlePropertyClick,
      },
    ];

    if (!hideExtraColumns) {
      const extraColumns = [
        {
          field: "MarketValue",
          header: "Market value",
          width: "130px",
          bodyTemplate: (rowData: IAnalyserDisplayData) => currencyTemplate("MarketValue", rowData),
        },
        {
          field: "LoanAmount",
          header: "Loan amount",
          width: "120px",
          bodyTemplate: (rowData: IAnalyserDisplayData) => currencyTemplate("LoanAmount", rowData),
        },
        {
          field: "CapitalGrowth",
          header: "Capital growth",
          width: "120px",
          bodyTemplate: (rowData: IAnalyserDisplayData) => currencyTemplate("CapitalGrowth", rowData),
        },
        {
          field: "CashflowPreTax",
          header: "Cashflow (pre-tax)",
          width: "130px",
          bodyTemplate: cashflowPreTaxTemplate,
        },
        {
          field: "GrossYield",
          header: "Yield% (pre-tax)",
          width: "130px",
          bodyTemplate: netYieldPreTaxTemplate,
        },
        {
          field: "PropertyAnalyserId",
          header: "",
          type: ColumnType.custom,
          onClick: handleRecentlyAnalysedProperty,
          template: (
            <i
              id="rana-icon-remove"
              data-testid="rana-icon-remove"
              className="pi pi-minus-circle text-[#4E9FBA] cursor-pointer"
              style={{ fontSize: "1rem" }}
              title="Remove from recently analysed"
            ></i>
          ),
          width: "50px",
        },
      ];

      return [...baseColumns, ...extraColumns];
    }

    return baseColumns;
  }, [hideExtraColumns, handlePropertyClick, handleRecentlyAnalysedProperty]);

  if (!data || data.length === 0) {
    return <div className="text-center">You have no saved property analysis.</div>;
  }

  return (
    <>
      <REIToastContainer toastReference={toastRef} />
      {recentlyAnalysedProperties && (
        <div
          className={`${
            !hideExtraColumns ? "overflow-y-auto min-h-[164px]" : "overflow-y-auto max-h-[100px]"
          } altRowShade`}
        >
          <REIDataTable
            data={recentlyAnalysedProperties}
            columns={columns}
            actions={undefined}
            customclass={hideExtraColumns ? "hide-datatable-header" : ""}
          />
        </div>
      )}
    </>
  );
};

export default RecentlyAnalysed;
